import { type ReactNode, useEffect } from "react";
import { useReducedMotion } from "react-native-reanimated";
import { LinearGradient } from "@tamagui/linear-gradient";
import { Lock as LockIcon } from "@tamagui/lucide-icons";
import { Body3, Display2, focusVisibleRingStyle, GrantedTextLogo, getTokenValue, Interface2, isWeb, ScrollView, TextSpan, Theme, Title5, textShadow, useInteractiveElement, useMedia, validToken, XStack, YStack } from "@medbillai/ui";
import { GLASS_BLUR, GLASS_FILL, GLASS_STROKE } from "./glass";

/** The hero's campaign dressing: everything that changes between funnels. */
export type IntakeLandingHero = {
  /** One photo per breakpoint, painted behind the scrim inside the hero frame. */
  image: {
    stacked: string;
    desktop: string;
    /** CSS `background-position` for the stacked plate; the desktop one is centred. */
    stackedPosition?: string;
  };
  /** Split so a figure can be picked out in the brand lime. */
  headline: {
    before: string;
    highlight: string;
    after?: string;
  };
  stats: {
    value: string;
    label: string;
    icon?: ReactNode;
  }[];
  trust: string;
};
const CARD_ID = "intake-landing-card";

// Floor/cap for the stacked hero; the card takes what its question needs.
const HERO_MIN_HEIGHT = 408;
const HERO_MAX_HEIGHT = 568;
const CARD_RADIUS = Number(getTokenValue("$sp4", "space"));

// Mobile only: the card overlaps the photo by its radius so the corners show photo; the hero copy leaves room above it.
const CARD_OVERLAP = CARD_RADIUS;
const HERO_TEXT_BOTTOM_INSET = CARD_OVERLAP + Number(getTokenValue("$sp3", "space"));

// Measure forces the two-line wrap the marketing hero uses.
const DESKTOP_HEADLINE = {
  fontSize: 44,
  lineHeight: 48,
  maxWidth: 440,
  textAlign: "left",
  alignSelf: "auto"
} as const;

// Onboarding chrome's measure, so the copy doesn't shift entering the flow.
const LANDING_MAX_WIDTH = 456;

// Same cap as `screens/sign-up-onboarding/layout.web.tsx`. The photo column is
// wider than signup's 44% on purpose (GRO-880): the landing leads with it.
const DESKTOP_MAX_WIDTH = 1650;
const HERO_COLUMN_WIDTH = "50%";
const LOGO_MOBILE = {
  width: 90,
  height: 18
};
const LOGO_DESKTOP = {
  width: 110,
  height: 22
};

// 1px ring in the photo's dark tone hides a sub-pixel clip seam on the right edge.
const HERO_EDGE = "#2A2926";

/** A background, not an `<img>`, so a hidden breakpoint's plate is never fetched. Sized to the box: the box tracks viewport height, a self-sized plate tracks width. */
function Plate({
  src,
  position = "center",
  ...visibility
}: {
  src: string;
  position?: string;
  display?: "none";
  $gtSm: {
    display: "none" | "flex";
  };
}) {
  return <YStack fullscreen {...visibility} style={{
    backgroundImage: `url(${src})`,
    backgroundSize: "cover",
    backgroundPosition: position
  }} data-sentry-element="YStack" data-sentry-component="Plate" data-sentry-source-file="IntakeLanding.tsx" />;
}
function HeroPlates({
  stacked,
  desktop,
  stackedPosition
}: IntakeLandingHero["image"]) {
  return <>
      <Plate src={stacked} position={stackedPosition} $gtSm={{
      display: "none"
    }} data-sentry-element="Plate" data-sentry-source-file="IntakeLanding.tsx" />
      <Plate src={desktop} display="none" $gtSm={{
      display: "flex"
    }} data-sentry-element="Plate" data-sentry-source-file="IntakeLanding.tsx" />
    </>;
}

// Stacked plate only: the desktop photo already carries its own contrast.
function HeroScrim() {
  return <LinearGradient fullscreen $gtSm={{
    display: "none"
  }} colors={["rgba(0, 0, 0, 0.28)", "rgba(0, 0, 0, 0)", "rgba(0, 0, 0, 0.08)", "rgba(0, 0, 0, 0.25)", "rgba(0, 0, 0, 0.45)"]} locations={[0, 0.2, 0.5, 0.72, 1]} start={{
    x: 0,
    y: 0
  }} end={{
    x: 0,
    y: 1
  }} data-sentry-element="LinearGradient" data-sentry-component="HeroScrim" data-sentry-source-file="IntakeLanding.tsx" />;
}

/** The brand strip above the hero. */
function Banner({
  children
}: {
  children: string;
}) {
  return <YStack paddingVertical="$sp1" paddingHorizontal="$sp2.5" backgroundColor="$bgBrandStrong"
  // iOS Safari tints the status bar from a sticky element at the top edge.
  position="sticky" top={0} data-sentry-element="YStack" data-sentry-component="Banner" data-sentry-source-file="IntakeLanding.tsx">
      <Body3 color="$textOnBrand" textAlign="center" $gtSm={{
      size: "$body2"
    }} data-sentry-element="Body3" data-sentry-source-file="IntakeLanding.tsx">
        {children}
      </Body3>
    </YStack>;
}
function StatCell({
  value,
  label,
  icon,
  divided
}: {
  value: string;
  label: string;
  icon?: ReactNode;
  divided?: boolean;
}) {
  return <YStack flex={1} alignItems="center" gap="$sp0.25" borderLeftWidth={divided ? "$border2" : 0} borderLeftColor={GLASS_STROKE} data-sentry-element="YStack" data-sentry-component="StatCell" data-sentry-source-file="IntakeLanding.tsx">
      <XStack alignItems="center" gap="$sp0.5" data-sentry-element="XStack" data-sentry-source-file="IntakeLanding.tsx">
        {icon}
        <Title5 color="$textInverse" data-sentry-element="Title5" data-sentry-source-file="IntakeLanding.tsx">{value}</Title5>
      </XStack>
      <Body3 color="$textInverse" data-sentry-element="Body3" data-sentry-source-file="IntakeLanding.tsx">{label}</Body3>
    </YStack>;
}
function Stats({
  stats
}: {
  stats: IntakeLandingHero["stats"];
}) {
  return <XStack marginTop="$sp2" paddingVertical="$sp1.5" paddingHorizontal="$sp0.75" backgroundColor={GLASS_FILL} borderWidth="$border2" borderColor={GLASS_STROKE} borderRadius="$br2" style={GLASS_BLUR} data-sentry-element="XStack" data-sentry-component="Stats" data-sentry-source-file="IntakeLanding.tsx">
      {stats.map((stat, index) => <StatCell key={stat.label} {...stat} divided={index > 0} />)}
    </XStack>;
}
function TrustLine({
  trust
}: {
  trust: string;
}) {
  return <XStack marginTop="$sp1.5" alignSelf="center" alignItems="center" gap="$sp0.5" data-sentry-element="XStack" data-sentry-component="TrustLine" data-sentry-source-file="IntakeLanding.tsx">
      <LockIcon size="$iconXSmall" color="$iconInverse" data-sentry-element="LockIcon" data-sentry-source-file="IntakeLanding.tsx" />
      <Body3 color="$textInverse" data-sentry-element="Body3" data-sentry-source-file="IntakeLanding.tsx">{trust}</Body3>
    </XStack>;
}
function Segment({
  n,
  goTo
}: {
  n: number;
  goTo?: () => void;
}) {
  // A real <button> when pressable, with Enter/Space handled by the hook.
  const {
    interactiveProps,
    renderTag
  } = useInteractiveElement({
    kind: goTo ? "button" : "static",
    onPress: goTo
  });
  return <XStack render={renderTag}
  // Equal segments so the numbers space evenly and the fill lands on a boundary; the track takes its size from them.
  width="$si7" paddingVertical="$sp1" alignItems="center" justifyContent="center" aria-label={goTo ? `Back to step ${n}` : undefined} borderRadius="$brFull" cursor={goTo ? "pointer" : "default"} hoverStyle={goTo ? {
    backgroundColor: "$utilityOverlayMedium"
  } : undefined} pressStyle={goTo ? {
    backgroundColor: "$utilityOverlayMedium"
  } : undefined} focusVisibleStyle={focusVisibleRingStyle} {...interactiveProps} data-sentry-element="XStack" data-sentry-component="Segment" data-sentry-source-file="IntakeLanding.tsx">
      <Interface2 color="$textDefault" data-sentry-element="Interface2" data-sentry-source-file="IntakeLanding.tsx">{n}</Interface2>
    </XStack>;
}
function StepPill<Step extends string>({
  steps,
  step,
  onStepPress
}: {
  steps: readonly Step[];
  /** 1-based position of the question on screen. */
  step?: number;
  onStepPress?: (target: Step) => void;
}) {
  const count = steps.length;
  const reducedMotion = useReducedMotion();
  return <XStack alignSelf="center" borderRadius="$brFull" backgroundColor="$bgBrand" role="group" aria-label={step === undefined ? undefined : `Step ${step} of ${count}`} data-sentry-element="XStack" data-sentry-component="StepPill" data-sentry-source-file="IntakeLanding.tsx">
      {/* Counts the question on screen, as the prototype and the flow's own progress bar do. */}
      <YStack position="absolute" top={0} bottom={0} left={0} testID="intake-landing.step-fill" width={`${Number(((step ?? 0) / count * 100).toFixed(2))}%`} borderRadius="$brFull" backgroundColor="$bgBrandStrongHover" transition={reducedMotion ? undefined : "500ms easeOut"} data-sentry-element="YStack" data-sentry-source-file="IntakeLanding.tsx" />
      {steps.map((tag, index) => {
      const n = index + 1;
      const answered = step !== undefined && n < step;
      const goTo = answered && onStepPress ? () => onStepPress(tag) : undefined;
      return <Segment key={n} n={n} goTo={goTo} />;
    })}
    </XStack>;
}

/** Marketing front door for an intake funnel: the first question is answerable in the card over the hero. */
export function IntakeLanding<Step extends string>({
  children,
  hero,
  banner,
  steps,
  current,
  onStepPress
}: {
  children: ReactNode;
  hero: IntakeLandingHero;
  banner?: string;
  /** The questions the card asks, in order; `current` is the one on screen. */
  steps: readonly Step[];
  current?: Step;
  /** Reopens an already-answered step. Absent when there's nothing behind. */
  onStepPress?: (target: Step) => void;
}) {
  const {
    gtSm
  } = useMedia();
  const step = current === undefined ? undefined : steps.indexOf(current) + 1;

  // Advancing swaps the card in place; scroll it back into view. Web-only: the funnel has no native route.
  useEffect(() => {
    if (!isWeb || gtSm || step === undefined || step <= 1) {
      return;
    }
    document.getElementById(CARD_ID)?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  }, [step, gtSm]);
  return <Theme name="v2" data-sentry-element="Theme" data-sentry-component="IntakeLanding" data-sentry-source-file="IntakeLanding.tsx">
      <ScrollView backgroundColor="$surface2" flex={1} data-sentry-element="ScrollView" data-sentry-source-file="IntakeLanding.tsx">
        <YStack minHeight={validToken("100dvh")} data-sentry-element="YStack" data-sentry-source-file="IntakeLanding.tsx">
          {banner === undefined ? null : <Banner>{banner}</Banner>}
          {/* Anchored to the page, not the centred row, like the signed-out shell. Two nodes rather than `useMedia()`: this subtree is server-rendered. */}
          <YStack flexGrow={1} data-sentry-element="YStack" data-sentry-source-file="IntakeLanding.tsx">
            <YStack position="absolute" top="$sp4" left="$sp5" zIndex={1} display="none" pointerEvents="none" $gtSm={{
            display: "flex",
            ...LOGO_DESKTOP
          }} data-sentry-element="YStack" data-sentry-source-file="IntakeLanding.tsx">
              <GrantedTextLogo width="100%" height="100%" data-sentry-element="GrantedTextLogo" data-sentry-source-file="IntakeLanding.tsx" />
            </YStack>
            <YStack width="100%" alignSelf="center" flexGrow={1}
          // `row-reverse`: the card stays second in source (phone reading order) but lands in the left column.
          $gtSm={{
            flexDirection: "row-reverse",
            maxWidth: DESKTOP_MAX_WIDTH
          }} data-sentry-element="YStack" data-sentry-source-file="IntakeLanding.tsx">
              <YStack flexGrow={1} flexShrink={1} flexBasis={0} minHeight={HERO_MIN_HEIGHT} maxHeight={HERO_MAX_HEIGHT}
            // Fixed width so the question column absorbs a wider viewport. `paddingLeft` faces the card under `row-reverse`.
            $gtSm={{
              flexGrow: 0,
              flexShrink: 0,
              flexBasis: HERO_COLUMN_WIDTH,
              minHeight: 0,
              maxHeight: "100%",
              padding: "$sp4",
              paddingLeft: "$sp2"
            }} data-sentry-element="YStack" data-sentry-source-file="IntakeLanding.tsx">
                {/* Clip on the frame, not the padded column, so the radius doesn't cut the padding. */}
                <YStack flex={1} overflow="hidden"
              // Offset so the ring doesn't paint above the top edge against the banner.
              boxShadow={`0 1px 0 1px ${HERO_EDGE}`} $gtSm={{
                boxShadow: "none",
                borderRadius: "$br6"
              }} data-sentry-element="YStack" data-sentry-source-file="IntakeLanding.tsx">
                  <HeroPlates {...hero.image} data-sentry-element="HeroPlates" data-sentry-source-file="IntakeLanding.tsx" />
                  <HeroScrim data-sentry-element="HeroScrim" data-sentry-source-file="IntakeLanding.tsx" />
                  {/* On the frame, not in the text column: that column centres between the phone measure and $gtSm, and the logo stays on the edge. */}
                  <YStack position="absolute" top="$sp2.5" left="$sp2.5" {...LOGO_MOBILE} $gtSm={{
                  display: "none"
                }} data-sentry-element="YStack" data-sentry-source-file="IntakeLanding.tsx">
                    <GrantedTextLogo width="100%" height="100%" tone="inverse" data-sentry-element="GrantedTextLogo" data-sentry-source-file="IntakeLanding.tsx" />
                  </YStack>
                  <YStack flex={1} width="100%" maxWidth={LANDING_MAX_WIDTH} alignSelf="center" paddingTop="$sp2.5" paddingHorizontal="$sp2.5" paddingBottom={HERO_TEXT_BOTTOM_INSET}
                // No overlap side by side; the half-width column is already narrower than the cap.
                $gtSm={{
                  padding: "$sp4",
                  maxWidth: "100%"
                }} data-sentry-element="YStack" data-sentry-source-file="IntakeLanding.tsx">
                    <Display2 marginTop="auto" paddingTop={0} lineHeight={40} letterSpacing={-0.4} textAlign="center" alignSelf="center" color="$textInverse" {...textShadow} $gtSm={DESKTOP_HEADLINE} data-sentry-element="Display2" data-sentry-source-file="IntakeLanding.tsx">
                      {hero.headline.before}
                      <TextSpan color="$bgBrandStrong" data-sentry-element="TextSpan" data-sentry-source-file="IntakeLanding.tsx">
                        {hero.headline.highlight}
                      </TextSpan>
                      {hero.headline.after}
                    </Display2>
                    <Stats stats={hero.stats} data-sentry-element="Stats" data-sentry-source-file="IntakeLanding.tsx" />
                    <TrustLine trust={hero.trust} data-sentry-element="TrustLine" data-sentry-source-file="IntakeLanding.tsx" />
                  </YStack>
                </YStack>
              </YStack>
              <YStack id={CARD_ID} flexShrink={0} marginTop={-CARD_OVERLAP} backgroundColor="$surface2" borderTopLeftRadius={CARD_RADIUS} borderTopRightRadius={CARD_RADIUS} paddingTop="$sp3" paddingBottom="$sp2.5"
            // Side by side there is nothing to overlap, so the card is simply the left column.
            $gtSm={{
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: 0,
              marginTop: 0,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              padding: "$sp4",
              justifyContent: "center"
            }} data-sentry-element="YStack" data-sentry-source-file="IntakeLanding.tsx">
                {/* Carries the measure so the question keeps its mobile line length. */}
                <YStack width="100%" maxWidth={LANDING_MAX_WIDTH} alignSelf="center" paddingHorizontal="$sp2.5" gap="$sp2" data-sentry-element="YStack" data-sentry-source-file="IntakeLanding.tsx">
                  {steps.length > 1 ? <StepPill steps={steps} step={step} onStepPress={onStepPress} /> : null}
                  {children}
                </YStack>
              </YStack>
            </YStack>
          </YStack>
        </YStack>
      </ScrollView>
    </Theme>;
}